import type { Tom } from "./tons"
import { FUNDO, TRACO } from "./tons"

export interface Fatia {
    rotulo: string
    valor: number
    tom: Tom
    explicacao: string
}

const RAIO = 60
const ESPESSURA = 22
const PERIMETRO = 2 * Math.PI * RAIO

// Uma rosca só se justifica quando as fatias somam o todo — aqui somam: cada pedido termina em
// exatamente um desfecho. É a mesma jornada do funil, vista como partição em vez de sequência:
// o funil responde "quanto sobrou em cada degrau", a rosca responde "de tudo que entrou, onde
// parou". Duas perguntas diferentes, por isso as duas coisas ficam na página.
//
// Desenhada em SVG e não em conic-gradient porque cada fatia precisa do próprio rótulo acessível
// — um gradiente é uma imagem só, sem partes que um leitor de tela possa nomear.
export const Rosca = ({
    fatias,
    centro,
    nota,
}: { fatias: Fatia[]; centro: string; nota: string }) => {
    const total = fatias.reduce((soma, fatia) => soma + fatia.valor, 0)
    const visiveis = fatias.filter((fatia) => fatia.valor > 0)

    let percorrido = 0

    return (
        <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-center">
            <svg
                viewBox="0 0 160 160"
                className="h-40 w-40 shrink-0 -rotate-90"
                role="img"
                aria-label={visiveis
                    .map((fatia) => `${fatia.rotulo}: ${fatia.valor} de ${total}`)
                    .join("; ")}
            >
                <circle
                    cx="80"
                    cy="80"
                    r={RAIO}
                    fill="none"
                    strokeWidth={ESPESSURA}
                    className="stroke-sunk"
                />
                {visiveis.map((fatia) => {
                    const fracao = fatia.valor / total
                    const deslocamento = -percorrido * PERIMETRO
                    percorrido += fracao
                    return (
                        <circle
                            key={fatia.rotulo}
                            cx="80"
                            cy="80"
                            r={RAIO}
                            fill="none"
                            strokeWidth={ESPESSURA}
                            className={TRACO[fatia.tom]}
                            strokeDasharray={`${fracao * PERIMETRO} ${PERIMETRO}`}
                            strokeDashoffset={deslocamento}
                        />
                    )
                })}
                {/* O contra-giro devolve o texto à horizontal: quem gira é o grupo todo. */}
                <g className="rotate-90" style={{ transformOrigin: "80px 80px" }}>
                    <text
                        x="80"
                        y="76"
                        textAnchor="middle"
                        className="fill-ink font-num text-2xl font-bold tabular-nums"
                    >
                        {centro}
                    </text>
                    <text x="80" y="94" textAnchor="middle" className="fill-faint text-[10px]">
                        {nota}
                    </text>
                </g>
            </svg>

            <ul className="w-full space-y-2.5">
                {fatias.map((fatia) => (
                    <li key={fatia.rotulo} className="text-sm">
                        <div className="flex items-baseline gap-2">
                            <span
                                aria-hidden="true"
                                className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${FUNDO[fatia.tom]}`}
                            />
                            <span className="flex-1 font-medium text-soft">{fatia.rotulo}</span>
                            <span className="tabular-nums font-semibold text-ink">
                                {fatia.valor}
                            </span>
                            <span className="w-10 text-right tabular-nums text-faint">
                                {total ? Math.round((fatia.valor / total) * 100) : 0}%
                            </span>
                        </div>
                        <p className="ml-[1.125rem] text-xs text-faint">{fatia.explicacao}</p>
                    </li>
                ))}
            </ul>
        </div>
    )
}
